import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import { useState } from "react";
import { canvasToolbarIconClassName } from "../../components/toolbar/canvasToolbarIcon";
import { canvasToolbarButtonVariants } from "../../components/toolbar/canvasToolbarStyles";

export type ArrowEndStyle = "none" | "arrow" | "arrow-open";

interface ArrowEndOption {
  value: ArrowEndStyle;
  label: string;
  /** End-oriented preview (arrow on the right). */
  preview: React.ReactNode;
  /** Start-oriented preview (arrow on the left, mirrored). */
  previewStart: React.ReactNode;
}

const glyph = canvasToolbarIconClassName;

/** 16×16 arrow-end previews — same slot as other toolbar glyphs. */
const ArrowPreviewSolid = () => (
  <svg className={glyph} viewBox="0 0 16 16" fill="none" aria-hidden>
    <line
      x1="1"
      y1="8"
      x2="9"
      y2="8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path d="M 8 5 L 14 8 L 8 11 Z" fill="currentColor" />
  </svg>
);

const ArrowPreviewSolidStart = () => (
  <svg className={glyph} viewBox="0 0 16 16" fill="none" aria-hidden>
    <line
      x1="7"
      y1="8"
      x2="15"
      y2="8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path d="M 8 5 L 2 8 L 8 11 Z" fill="currentColor" />
  </svg>
);

const ArrowPreviewOpen = () => (
  <svg className={glyph} viewBox="0 0 16 16" fill="none" aria-hidden>
    <line
      x1="1"
      y1="8"
      x2="9"
      y2="8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <polyline
      points="8,5 14,8 8,11"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const ArrowPreviewOpenStart = () => (
  <svg className={glyph} viewBox="0 0 16 16" fill="none" aria-hidden>
    <line
      x1="7"
      y1="8"
      x2="15"
      y2="8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <polyline
      points="8,5 2,8 8,11"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const ArrowPreviewNone = () => (
  <svg className={glyph} viewBox="0 0 16 16" fill="none" aria-hidden>
    <line
      x1="2"
      y1="8"
      x2="14"
      y2="8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const ARROW_END_OPTIONS: ArrowEndOption[] = [
  {
    value: "none",
    label: "None",
    preview: <ArrowPreviewNone />,
    previewStart: <ArrowPreviewNone />,
  },
  {
    value: "arrow",
    label: "Solid arrow",
    preview: <ArrowPreviewSolid />,
    previewStart: <ArrowPreviewSolidStart />,
  },
  {
    value: "arrow-open",
    label: "Open arrow",
    preview: <ArrowPreviewOpen />,
    previewStart: <ArrowPreviewOpenStart />,
  },
];

function getPreview(value: ArrowEndStyle, isStart: boolean): React.ReactNode {
  const opt = ARROW_END_OPTIONS.find((o) => o.value === value);
  if (!opt) return <ArrowPreviewNone />;
  return isStart ? opt.previewStart : opt.preview;
}

interface ArrowEndPickerProps {
  value: ArrowEndStyle;
  onChange: (value: ArrowEndStyle) => void;
  onOpenChange?: (open: boolean) => void;
  /** "Start" or "End" — shown in the tooltip */
  label: string;
  /** When true, previews are mirrored so the arrowhead points leftward (start of line). */
  isStart?: boolean;
}

/**
 * Per-endpoint arrow style picker.
 * Replaces the combined arrowMode: "none" | "end" | "both".
 */
export function ArrowEndPicker({
  value,
  onChange,
  onOpenChange,
  label,
  isStart = false,
}: ArrowEndPickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              className={canvasToolbarButtonVariants()}
              onClick={(e) => e.stopPropagation()}
            >
              {getPreview(value, isStart)}
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">{label} arrow</TooltipContent>
      </Tooltip>
      <PopoverContent
        className="w-40 p-1 rounded-xl"
        align="center"
        side="top"
        sideOffset={14}
        onClick={(e) => e.stopPropagation()}
      >
        {ARROW_END_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => {
              onChange(opt.value);
              handleOpenChange(false);
            }}
            className={cn(
              "flex w-full items-center gap-2 rounded-lg px-3 py-1.5 transition-colors text-left",
              "hover:bg-n-95/8 dark:hover:bg-n-95/10",
              value === opt.value && "bg-primary/10 text-primary font-medium",
            )}
          >
            {isStart ? opt.previewStart : opt.preview}
            <span className="text-xs">{opt.label}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
